import React, { useState, useEffect, useCallback } from 'react';
import { Input, Select, InputNumber, Button, Card, Row, Col, message, Steps,
  Table, Typography, Alert, Space, Tag, Spin, Upload } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, PlusOutlined, DeleteOutlined,
  UploadOutlined, StarFilled, StarOutlined, CalculatorOutlined, FilePdfOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import useAvisoAlteracoes from '../hooks/useAvisoAlteracoes';
import CabecalhoLaudo from '../components/CabecalhoLaudo';

const { Text } = Typography;
const { Option } = Select;

// leitura pode ser numero, texto com virgula (digitando) ou S/A (sem acesso): S/A e vazio nao sao numero
const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(String(v).replace(',', '.'));
  return isFinite(n) ? n : null;
};
const txtLeitura = (v) => (v === null || v === undefined ? '' : typeof v === 'number' ? String(v).replace('.', ',') : String(v));
const normLeitura = (t) => {
  const s = String(t || '').toUpperCase().replace(/\s/g, '');
  if (s === '') return null;
  if (s === 'SA' || s === 'S/A') return 'S/A';
  if (s === 'S' || s === 'S/') return s; // digitando S/A
  return s.replace(/[^0-9.,]/g, '') || null;
};
const fmt = (n, c) => (n === null || n === undefined || !isFinite(Number(n))
  ? '—' : Number(n).toFixed(c === undefined ? 2 : c).replace('.', ','));
const aj = (t) => <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2, lineHeight: 1.3 }}>{t}</div>;

const ROTULO_PADRAO = {
  medidor_espessura: 'Medidor de espessura', manometro: 'Manômetro',
  manovacuometro: 'Manovacuômetro', termometro: 'Termômetro', incubadora: 'Incubadora'
};
const nomePadrao = (t) => ROTULO_PADRAO[t] ||
  String(t || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

const EQUIPAMENTOS = ['Tubulação', 'Vaso de Pressão', 'Tanque', 'Caldeira', 'Autoclave', 'Outro'];
const UNI_P = [['kgf', 'kgf/cm²'], ['psi', 'psi'], ['bar', 'bar'], ['mpa', 'MPa']];
// o mais usado vai para o topo: rolar a lista a cada laudo e onde se erra o
// material sem perceber
const MATERIAIS_NO_TOPO = ['ASTM A 36'];
const ordenarMateriais = (lista) => {
  const topo = [];
  const resto = [];
  (lista || []).forEach((m) => (MATERIAIS_NO_TOPO.indexOf(m.nome) >= 0 ? topo : resto).push(m));
  return topo.concat(resto);
};

// mesma regra do servidor: verde acima da minima, vermelho no limite critico
// ou abaixo, amarelo entre os dois
const COR = { verde: '#f6ffed', amarelo: '#fffbe6', vermelho: '#fff1f0' };
const COR_TXT = { verde: '#237804', amarelo: '#ad6800', vermelho: '#a8071a' };
const classificar = (valor, minima, pct) => {
  const v = num(valor);
  const m = num(minima);
  if (v === null) return null;
  if (m === null) return null;
  if (v > m) return 'verde';
  return v <= m * ((pct === null || pct === undefined ? 75 : Number(pct)) / 100) ? 'vermelho' : 'amarelo';
};

export default function InspecaoEspessura() {
  const nav = useNavigate();
  const { id } = useParams();

  const [d, setD] = useState(null);
  const naoSalvo = useAvisoAlteracoes(d);
  const [trechos, setTrechos] = useState([]);
  const [step, setStep] = useState(0);
  const [salvando, setSalvando] = useState(false);
  const [materiais, setMateriais] = useState([]);
  const [fluidos, setFluidos] = useState([]);
  const [padroes, setPadroes] = useState([]);
  const [avisos, setAvisos] = useState([]);
  const [calc, setCalc] = useState(null);
  const [imgs, setImgs] = useState({});
  const [, setCroq] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [faixa, setFaixa] = useState({ de: 0, ate: 10, passo: 1 });

  const set = (campo, v) => setD((x) => ({ ...x, [campo]: v }));

  const miniatura = useCallback(async (tipo, idx) => {
    try {
      const r = await api.get('/inspecoes-espessura/' + id + '/' + tipo + '/' + idx, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      if (tipo === 'fotos') setImgs((p) => ({ ...p, [idx]: url }));
      else setCroq((p) => ({ ...p, [idx]: url }));
    } catch (e) { /* miniatura fica vazia */ }
  }, [id]);

  const carregar = useCallback(async () => {
    try {
      const r = await api.get('/inspecoes-espessura/' + id);
      const l = r.data;
      setTrechos((l.trechos || []).map((t) => ({ ...t, leituras: t.leituras || [] })));
      delete l.trechos;
      ['pmo_unidade', 'pmta_unidade', 'pth_unidade'].forEach((k) => { if (!l[k]) l[k] = 'kgf'; });
      if (!l.status) l.status = 'rascunho';
      if (l.limite_critico_pct === null || l.limite_critico_pct === undefined) l.limite_critico_pct = 75;
      setD(l);
      (l.fotos || []).forEach((f, i) => miniatura('fotos', i));
      (l.croquis || []).forEach((f, i) => miniatura('croquis', i));
    } catch { message.error('Erro ao carregar o laudo'); }
  }, [id, miniatura]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    api.get('/inspecoes-espessura/apoio/materiais').then((r) => setMateriais(r.data || [])).catch(() => {});
    api.get('/inspecoes-espessura/apoio/padroes').then((r) => setPadroes(r.data || [])).catch(() => {});
    api.get('/classificacao-nr13/fluidos').then((r) => setFluidos(r.data || [])).catch(() => {});
  }, []);

  const recarregarCalculados = useCallback(async () => {
    try {
      const r = await api.get('/inspecoes-espessura/' + id);
      const l = r.data;
      setTrechos((l.trechos || []).map((t) => ({ ...t, leituras: t.leituras || [] })));
      setD((x) => ({ ...x, codigo: l.codigo, revisao: l.revisao, status: l.status || x.status,
        atualizado_em: l.atualizado_em, fotos: l.fotos, croquis: l.croquis }));
      (l.fotos || []).forEach((f, i) => miniatura('fotos', i));
      (l.croquis || []).forEach((f, i) => miniatura('croquis', i));
    } catch { /* mantem o que esta na tela */ }
  }, [id, miniatura]);

  const subir = (tipo) => async (file) => {
    const fd = new FormData(); fd.append('foto', file);
    setEnviando(true);
    try { await api.post('/inspecoes-espessura/' + id + '/' + tipo, fd); await recarregarCalculados(); }
    catch (e) { message.error('Erro ao enviar a imagem'); }
    setEnviando(false);
    return false;
  };
  const remover = (tipo) => async (idx) => {
    try {
      await api.delete('/inspecoes-espessura/' + id + '/' + tipo + '/' + idx);
      if (tipo === 'fotos') setImgs({}); else setCroq({});
      await recarregarCalculados();
    } catch (e) { message.error('Erro ao remover'); }
  };
  const definirCapa = async (idx) => {
    try { await api.put('/inspecoes-espessura/' + id + '/fotos/' + idx + '/principal'); await recarregarCalculados(); }
    catch (e) { message.error('Erro ao definir a capa'); }
  };

  // ------------------------------------------------------------ trechos
  const novoTrecho = () => setTrechos((ts) => ts.concat([{
    ordem: ts.length, nome: '', descricao: '', qtd_referencias: 4,
    espessura_nominal_mm: null, espessura_minima_mm: null, leituras: []
  }]));
  const mudaTrecho = (i, campo, v) => setTrechos((ts) => {
    const n = ts.slice(); n[i] = { ...n[i], [campo]: v }; return n;
  });
  const removeTrecho = (i) => setTrechos((ts) => ts.filter((x, k) => k !== i));

  const novaLinha = (i) => setTrechos((ts) => {
    const n = ts.slice();
    const t = { ...n[i] };
    const refs = Number(t.qtd_referencias) || 4;
    t.leituras = (t.leituras || []).concat([{ p: '', v: Array(refs).fill(null) }]);
    n[i] = t; return n;
  });
  const removeLinha = (i, k) => setTrechos((ts) => {
    const n = ts.slice();
    n[i] = { ...n[i], leituras: (n[i].leituras || []).filter((x, j) => j !== k) };
    return n;
  });
  const gerarFaixa = (i) => setTrechos((ts) => {
    const n = ts.slice();
    const t = { ...n[i] };
    const refs = Number(t.qtd_referencias) || 4;
    const de = Number(faixa.de);
    const ate = Number(faixa.ate);
    const passo = Number(faixa.passo) || 1;
    if (!isFinite(de) || !isFinite(ate) || passo <= 0 || ate < de) {
      message.warning('Confira de, até e passo.');
      return ts;
    }
    if ((ate - de) / passo > 500) {
      message.warning('Faixa grande demais: mais de 500 posições.');
      return ts;
    }
    const existentes = new Set((t.leituras || []).map((l) => String(l.p)));
    const novas = [];
    for (let p = de; p <= ate + 1e-9; p += passo) {
      const rot = String(Math.round(p * 1000) / 1000);
      if (!existentes.has(rot)) novas.push({ p: rot, v: Array(refs).fill(null) });
    }
    t.leituras = (t.leituras || []).concat(novas);
    n[i] = t;
    message.success(novas.length + ' posição(ões) criada(s).');
    return n;
  });
  const mudaLeitura = (i, k, j, v) => setTrechos((ts) => {
    const n = ts.slice();
    const t = { ...n[i] };
    const linhas = (t.leituras || []).slice();
    const linha = { ...linhas[k] };
    const vals = (linha.v || []).slice();
    vals[j] = v;
    linha.v = vals;
    linhas[k] = linha;
    t.leituras = linhas;
    n[i] = t; return n;
  });
  const mudaPosicao = (i, k, v) => setTrechos((ts) => {
    const n = ts.slice();
    const t = { ...n[i] };
    const linhas = (t.leituras || []).slice();
    linhas[k] = { ...linhas[k], p: v };
    t.leituras = linhas;
    n[i] = t; return n;
  });

  // ------------------------------------------------------------ salvar
  const salvar = async (aviso = true) => {
    if (!d) return null;
    setSalvando(true);
    try {
      const corpo = { ...d };
      ['id', 'codigo', 'criado_em', 'atualizado_em', 'disponivel_portal', 'app_uuid',
        'razao_social', 'nome_cliente', 'cnpj', 'art_cliente', 'cnpj_faturamento',
        'os_numero', 'end_atend_logradouro', 'end_atend_numero', 'end_atend_bairro',
        'end_atend_cidade', 'end_atend_estado', 'end_atend_cep', 'fotos', 'croquis', 'calculo']
        .forEach((k) => delete corpo[k]);
      corpo.trechos = trechos.map((t, k) => ({ ...t, ordem: k }));
      const r = await api.put('/inspecoes-espessura/' + id, corpo);
      const c = r.data.calculo || null;
      setCalc(c);
      setAvisos((c && c.avisos) || []);
      await recarregarCalculados();
      naoSalvo.limpo();
      if (aviso) message.success('Laudo salvo');
      return c;
    } catch (e) {
      message.error('Erro ao salvar: ' + (e?.response?.data?.erro || e.message));
      return null;
    } finally { setSalvando(false); }
  };

  const gerarPdf = async () => {
    message.loading({ content: 'Gerando o PDF...', key: 'pdf', duration: 0 });
    try {
      await salvar(false);
      const r = await api.get('/inspecoes-espessura/' + id + '/pdf', { responseType: 'blob' });
      message.destroy('pdf');
      window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank');
      await recarregarCalculados();
    } catch (e) {
      message.destroy('pdf');
      let msg = 'Erro ao gerar o PDF';
      try { msg = JSON.parse(await e.response.data.text()).erro || msg; } catch (x) { /* nao e json */ }
      message.error(msg);
    }
  };

  if (!d) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;

  const pct = d.limite_critico_pct;
  const etapas = [{ title: 'Identificação' }, { title: 'Equipamento' }, { title: 'Ensaio' },
    { title: 'Medição' }, { title: 'Objetivo' }, { title: 'Foto da capa' }, { title: 'Resultado' }];

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => nav('/laudos')}>Voltar</Button>
        <b>Medição de Espessura</b>
        {d.codigo && <Tag color="blue">{d.codigo}</Tag>}
        {calc && calc.total_criticos > 0 && <Tag color="red">{calc.total_criticos} crítica(s)</Tag>}
        {calc && calc.total_abaixo_minima > 0 &&
          <Tag color="orange">{calc.total_abaixo_minima} abaixo da mínima</Tag>}
      </Space>

      {avisos.length > 0 && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="Pontos de atenção"
          description={<ul style={{ margin: 0, paddingLeft: 18 }}>
            {avisos.map((a, i) => <li key={i}>{a}</li>)}</ul>} />
      )}

      <Steps current={step} size="small" items={etapas} onChange={setStep} style={{ marginBottom: 16 }} />

      {step === 0 && <CabecalhoLaudo d={d} set={set} semArt />}

      {step === 1 && (
        <Card title="Equipamento" size="small">
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <label>Tipo de equipamento</label>
              <Select style={{ width: '100%' }} value={d.tipo_equipamento || undefined}
                onChange={(v) => set('tipo_equipamento', v)}
                options={EQUIPAMENTOS.map((e) => ({ value: e, label: e }))} />
              {aj('A medição de espessura serve a qualquer equipamento.')}
            </Col>
            <Col span={6}>
              <label>TAG</label>
              <Input value={d.tag || ''} onChange={(e) => set('tag', e.target.value)} />
            </Col>

            <Col span={5}>
              <label>Nº de série</label>
              <Input value={d.numero_serie || ''} onChange={(e) => set('numero_serie', e.target.value)} />
            </Col>
            <Col span={5}>
              <label>Fabricante</label>
              <Input value={d.fabricante || ''} onChange={(e) => set('fabricante', e.target.value)} />
            </Col>
            <Col span={4}>
              <label>Ano de fabricação</label>
              <InputNumber style={{ width: '100%' }} min={1900} max={2100}
                value={num(d.ano_fabricacao)} onChange={(v) => set('ano_fabricacao', v)} />
            </Col>
            <Col span={5}>
              <label>Local de instalação</label>
              <Input value={d.local_instalacao || ''} onChange={(e) => set('local_instalacao', e.target.value)} />
            </Col>

            <Col span={6}>
              <label>Fluido</label>
              <Select style={{ width: '100%' }} allowClear showSearch optionFilterProp="children"
                value={d.fluido || undefined} onChange={(v) => set('fluido', v || null)}>
                {fluidos.map((f) => <Option key={f.id} value={f.nome}>{f.nome}</Option>)}
                <Option value="outro">Outro</Option>
              </Select>
            </Col>
            {d.fluido === 'outro' && (
              <Col span={6}>
                <label>Qual fluido</label>
                <Input value={d.fluido_outro || ''} onChange={(e) => set('fluido_outro', e.target.value)} />
              </Col>
            )}
            <Col span={3}>
              <label>PMO</label>
              <InputNumber style={{ width: '100%' }} min={0} step={0.1} decimalSeparator=","
                value={num(d.pmo)} onChange={(v) => set('pmo', v)} />
            </Col>
            <Col span={2}>
              <label>Un.</label>
              <Select style={{ width: '100%' }} value={d.pmo_unidade || 'kgf'}
                onChange={(v) => set('pmo_unidade', v)}
                options={UNI_P.map(([v, r]) => ({ value: v, label: r }))} />
            </Col>
            <Col span={3}>
              <label>PMTA</label>
              <InputNumber style={{ width: '100%' }} min={0} step={0.1} decimalSeparator=","
                value={num(d.pmta)} onChange={(v) => set('pmta', v)} />
              {aj('Vazio quando não houver: este laudo não calcula PMTA.')}
            </Col>
            <Col span={2}>
              <label>Un.</label>
              <Select style={{ width: '100%' }} value={d.pmta_unidade || 'kgf'}
                onChange={(v) => set('pmta_unidade', v)}
                options={UNI_P.map(([v, r]) => ({ value: v, label: r }))} />
            </Col>
            <Col span={3}>
              <label>PTH</label>
              <InputNumber style={{ width: '100%' }} min={0} step={0.1} decimalSeparator=","
                value={num(d.pth)} onChange={(v) => set('pth', v)} />
            </Col>
            <Col span={2}>
              <label>Un.</label>
              <Select style={{ width: '100%' }} value={d.pth_unidade || 'kgf'}
                onChange={(v) => set('pth_unidade', v)}
                options={UNI_P.map(([v, r]) => ({ value: v, label: r }))} />
            </Col>
            <Col span={6}>
              <label>Tipo de inspeção</label>
              <Select style={{ width: '100%' }} value={d.tipo_inspecao || undefined}
                onChange={(v) => set('tipo_inspecao', v)}
                options={[{ value: 'inicial', label: 'Inicial' }, { value: 'periodica', label: 'Periódica' },
                  { value: 'extraordinaria', label: 'Extraordinária' }]} />
            </Col>
          </Row>
        </Card>
      )}

      {step === 2 && (
        <Card title="Dados do ensaio" size="small">
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <label>Data de início</label>
              <Input type="date" value={(d.data_inicio || '').slice(0, 10)}
                onChange={(e) => set('data_inicio', e.target.value || null)} />
            </Col>
            <Col span={8}>
              <label>Data de término</label>
              <Input type="date" value={(d.data_termino || '').slice(0, 10)}
                onChange={(e) => set('data_termino', e.target.value || null)} />
            </Col>

            <Col span={10}>
              <label>Equipamento padrão utilizado</label>
              <Select style={{ width: '100%' }} allowClear showSearch optionFilterProp="children"
                value={d.medidor_modelo || undefined}
                onChange={(v) => {
                  const p = padroes.find((x) => x.identificacao === v);
                  setD((x) => ({ ...x, medidor_modelo: v,
                    medidor_serie: p ? p.certificado : x.medidor_serie,
                    medidor_padrao_tipo: p ? p.tipo : x.medidor_padrao_tipo }));
                }}>
                {padroes.map((p) => (
                  <Option key={p.identificacao} value={p.identificacao}>
                    {nomePadrao(p.tipo)} — {p.identificacao}{p.marca ? ' (' + p.marca + ')' : ''}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={4}>
              <label>Técnica</label>
              <Input value={d.tecnica || ''} onChange={(e) => set('tecnica', e.target.value)} />
            </Col>
            <Col span={4}>
              <label>Acoplante</label>
              <Input value={d.acoplante || ''} onChange={(e) => set('acoplante', e.target.value)} />
            </Col>
            <Col span={3}>
              <label>Temperatura</label>
              <Input value={d.temperatura || ''} onChange={(e) => set('temperatura', e.target.value)} />
            </Col>
            <Col span={3}>
              <label>Condição da superfície</label>
              <Input value={d.condicao_superficie || ''}
                onChange={(e) => set('condicao_superficie', e.target.value)} />
            </Col>

            <Col span={8}>
              <label>Material</label>
              <Select style={{ width: '100%' }} allowClear showSearch optionFilterProp="children"
                value={d.material_id || undefined}
                onChange={(v) => {
                  const m = materiais.find((x) => x.id === v);
                  setD((x) => ({ ...x, material_id: v, material: m ? m.nome : x.material }));
                }}>
                {ordenarMateriais(materiais).map((m) => <Option key={m.id} value={m.id}>{m.nome}</Option>)}
              </Select>
            </Col>
            <Col span={4}>
              <label>Material estimado?</label>
              <Select style={{ width: '100%' }}
                value={d.material_estimado === true ? true : false}
                onChange={(v) => set('material_estimado', v)}
                options={[{ value: false, label: 'Não' }, { value: true, label: 'Sim (estimado)' }]} />
              {aj('Marque quando o material não foi confirmado por documento ou ensaio.')}
            </Col>
            <Col span={8}>
              <label>Procedimento</label>
              <Input value={d.procedimento || ''} onChange={(e) => set('procedimento', e.target.value)} />
            </Col>
            <Col span={16}>
              <label>Texto do exame (vai impresso no laudo)</label>
              <Input.TextArea rows={3} value={d.exame_texto || ''}
                onChange={(e) => set('exame_texto', e.target.value)}
                placeholder="Deixe vazio para o laudo usar o texto padrão." />
            </Col>
          </Row>
        </Card>
      )}

      {step === 3 && (
        <Card title="Medição de espessura" size="small"
          extra={<Space>
            <Button icon={<PlusOutlined />} onClick={novoTrecho}>Adicionar trecho</Button>
            <Button type="primary" icon={<CalculatorOutlined />} onClick={() => salvar()}>
              Salvar e classificar
            </Button>
          </Space>}>
          <Row gutter={16} style={{ marginBottom: 12 }}>
            <Col span={6}>
              <label>Limite crítico (% da mínima)</label>
              <InputNumber style={{ width: '100%' }} min={1} max={100} step={1} decimalSeparator=","
                value={num(d.limite_critico_pct)} onChange={(v) => set('limite_critico_pct', v)} />
              {aj('Abaixo disso a medida fica vermelha. 75% reproduz o critério usado hoje: '
                + 'com mínima de 4,00 mm, vermelho a partir de 3,00 mm.')}
            </Col>
            <Col span={18}>
              <Alert type="info" showIcon
                message="Como as cores funcionam"
                description={'Verde acima da mínima do trecho. Amarelo abaixo da mínima, mas acima do '
                  + 'limite crítico. Vermelho no limite crítico ou abaixo. Atenção: amarelo já está '
                  + 'abaixo do que o cliente exigiu — o laudo conta esses pontos separadamente.'} />
            </Col>
          </Row>

          {trechos.length === 0 && <Text type="secondary">Nenhum trecho. Clique em Adicionar trecho.</Text>}

          {trechos.map((t, i) => {
            const refs = Number(t.qtd_referencias) || 4;
            return (
              <Card key={i} size="small" style={{ marginBottom: 12 }}
                title={<Space>
                  <span>{t.nome || 'Trecho ' + (i + 1)}</span>
                  {t.aprovado === true && <Tag color="green">Tudo acima da mínima</Tag>}
                  {t.aprovado === false && <Tag color="red">{t.qtd_abaixo_minima} abaixo da mínima</Tag>}
                  {t.menor_mm !== null && t.menor_mm !== undefined &&
                    <Tag>menor: {fmt(t.menor_mm, 2)} mm</Tag>}
                </Space>}
                extra={<Button danger size="small" icon={<DeleteOutlined />} onClick={() => removeTrecho(i)} />}>
                <Row gutter={[12, 12]} style={{ marginBottom: 10 }}>
                  <Col span={6}>
                    <label>Nome do trecho</label>
                    <Input value={t.nome || ''} onChange={(e) => mudaTrecho(i, 'nome', e.target.value)}
                      placeholder="Ex.: Tubulação 1" />
                  </Col>
                  <Col span={6}>
                    <label>Descrição</label>
                    <Input value={t.descricao || ''} onChange={(e) => mudaTrecho(i, 'descricao', e.target.value)} />
                  </Col>
                  <Col span={3}>
                    <label>Referências</label>
                    <InputNumber style={{ width: '100%' }} min={1} max={8}
                      value={refs} onChange={(v) => mudaTrecho(i, 'qtd_referencias', v)} />
                  </Col>
                  <Col span={3}>
                    <label>Nominal (mm)</label>
                    <InputNumber style={{ width: '100%' }} min={0} step={0.1} decimalSeparator=","
                      value={num(t.espessura_nominal_mm)}
                      onChange={(v) => mudaTrecho(i, 'espessura_nominal_mm', v)} />
                  </Col>
                  <Col span={3}>
                    <label>Mínima (mm)</label>
                    <InputNumber style={{ width: '100%' }} min={0} step={0.1} decimalSeparator=","
                      value={num(t.espessura_minima_mm)}
                      onChange={(v) => mudaTrecho(i, 'espessura_minima_mm', v)} />
                    {aj('Informada pelo cliente.')}
                  </Col>
                  <Col span={3}>
                    <label>Limite crítico</label>
                    <Input disabled value={t.espessura_minima_mm
                      ? fmt(Number(t.espessura_minima_mm) * (Number(pct) / 100), 2) + ' mm' : '—'} />
                  </Col>
                </Row>

                <Space style={{ marginBottom: 8 }}>
                  <span style={{ fontSize: 12, color: '#8c8c8c' }}>Gerar posições de</span>
                  <InputNumber size="small" style={{ width: 80 }} value={faixa.de}
                    decimalSeparator="," onChange={(v) => setFaixa((f) => ({ ...f, de: v }))} />
                  <span style={{ fontSize: 12, color: '#8c8c8c' }}>até</span>
                  <InputNumber size="small" style={{ width: 80 }} value={faixa.ate}
                    decimalSeparator="," onChange={(v) => setFaixa((f) => ({ ...f, ate: v }))} />
                  <span style={{ fontSize: 12, color: '#8c8c8c' }}>passo</span>
                  <InputNumber size="small" style={{ width: 80 }} value={faixa.passo}
                    decimalSeparator="," onChange={(v) => setFaixa((f) => ({ ...f, passo: v }))} />
                  <Button size="small" onClick={() => gerarFaixa(i)}>Gerar</Button>
                  <Button size="small" icon={<PlusOutlined />} onClick={() => novaLinha(i)}>Uma posição</Button>
                  <span style={{ fontSize: 12, color: '#8c8c8c' }}>Sem acesso ao ponto: digite S/A</span>
                </Space>

                <Table size="small" pagination={{ pageSize: 30, showSizeChanger: false }}
                  rowKey={(r, k) => k}
                  dataSource={(t.leituras || []).map((l, k) => ({ ...l, _k: k }))}
                  columns={[
                    { title: 'Posição', width: 110,
                      render: (_, r) => (
                        <Input size="small" value={r.p === null || r.p === undefined ? '' : r.p}
                          onChange={(e) => mudaPosicao(i, r._k, e.target.value)} />) }
                  ].concat(Array.from({ length: refs }, (x, j) => ({
                    title: 'Ref ' + (j + 1), width: 90,
                    render: (_, r) => {
                      const cor = classificar((r.v || [])[j], t.espessura_minima_mm, pct);
                      return (
                        <Input size="small" style={{ width: '100%',
                          background: cor ? COR[cor] : (txtLeitura((r.v || [])[j]) === 'S/A' ? '#f5f5f5' : undefined),
                          color: cor ? COR_TXT[cor] : undefined,
                          fontWeight: cor === 'vermelho' ? 'bold' : undefined }}
                          inputMode="decimal" placeholder=""
                          value={txtLeitura((r.v || [])[j])}
                          onChange={(e) => mudaLeitura(i, r._k, j, normLeitura(e.target.value))} />);
                    }
                  }))).concat([
                    { title: 'Menor', width: 80, align: 'right',
                      render: (_, r) => {
                        const vals = (r.v || []).map(num).filter((v) => v !== null && v > 0);
                        return vals.length ? fmt(Math.min.apply(null, vals), 2) : '—';
                      } },
                    { title: '', width: 40,
                      render: (_, r) => (
                        <Button type="text" size="small" danger icon={<DeleteOutlined />}
                          onClick={() => removeLinha(i, r._k)} />) }
                  ])} />
              </Card>
            );
          })}
        </Card>
      )}

      {step === 4 && (
        <Card title="Objetivo do relatório" size="small">
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <label>Objetivo (vai impresso no laudo)</label>
              <Input.TextArea rows={3} value={d.objetivo || ''}
                onChange={(e) => set('objetivo', e.target.value)}
                placeholder="Deixe vazio para o laudo usar o texto padrão." />
            </Col>
          </Row>
        </Card>
      )}

      {step === 5 && (
        <>
          <Card title="Foto da capa (a marcada com estrela, ou a primeira; não vai no corpo do laudo)" size="small"
            extra={<Upload beforeUpload={subir('fotos')} showUploadList={false} accept="image/*" multiple>
              <Button icon={<UploadOutlined />} loading={enviando}>Adicionar foto</Button>
            </Upload>}>
            <Row gutter={[12, 12]}>
              {(d.fotos || []).map((f, i) => (
                <Col span={6} key={i}>
                  <Card size="small" cover={imgs[i]
                    ? <img alt={f.nome} src={imgs[i]} style={{ height: 150, objectFit: 'cover' }} />
                    : <div style={{ height: 150, background: '#fafafa' }} />}
                    actions={[
                      <Button type="text" size="small" onClick={() => definirCapa(i)}
                        icon={f.principal ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />} />,
                      <Button type="text" size="small" danger icon={<DeleteOutlined />}
                        onClick={() => remover('fotos')(i)} />
                    ]}>
                    <Input size="small" placeholder="Legenda" defaultValue={f.legenda || ''}
                      onBlur={(e) => {
                        const novas = (d.fotos || []).slice();
                        novas[i] = { ...novas[i], legenda: e.target.value };
                        set('fotos', novas);
                      }} />
                  </Card>
                </Col>
              ))}
            </Row>
            {(d.fotos || []).length === 0 && <Text type="secondary">Nenhuma foto.</Text>}
          </Card>

        </>
      )}

      {step === 6 && (
        <Card title="Resultado e conclusão" size="small"
          extra={<Button icon={<FilePdfOutlined />} onClick={gerarPdf}>Gerar PDF</Button>}>
          {calc && (
            <Alert style={{ marginBottom: 16 }}
              type={calc.total_criticos > 0 ? 'error'
                : calc.total_abaixo_minima > 0 ? 'warning' : 'success'}
              showIcon
              message="Resumo da medição"
              description={calc.total_leituras + ' leitura(s) no total · '
                + calc.total_abaixo_minima + ' abaixo da mínima · '
                + calc.total_criticos + ' no vermelho.'} />
          )}
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <label>Status do laudo</label>
              <Select style={{ width: '100%' }} value={d.status || 'rascunho'}
                onChange={(v) => set('status', v)}
                options={[{ value: 'rascunho', label: 'Rascunho' },
                  { value: 'concluido', label: 'Concluído' }]} />
            </Col>
            <Col span={24}>
              <label>Conclusão</label>
              <Input.TextArea rows={4} value={d.conclusao || ''}
                onChange={(e) => set('conclusao', e.target.value)} />
            </Col>
          </Row>
        </Card>
      )}

      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Space>
          <Button disabled={step === 0} onClick={() => setStep(step - 1)}>Anterior</Button>
          <Button disabled={step === etapas.length - 1} onClick={() => setStep(step + 1)}>Próxima</Button>
        </Space>
        <Space>
          {step === etapas.length - 1 && (
            <Button icon={<FilePdfOutlined />} onClick={gerarPdf}>Gerar PDF</Button>
          )}
          <Button type="primary" icon={<SaveOutlined />} loading={salvando} onClick={() => salvar()}>
            Salvar
          </Button>
        </Space>
      </div>
    </div>
  );
}
